import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  useTryOnPhotosQuery,
  useAddTryOnPhoto,
  useUpdateTryOnPhoto,
  useDeleteTryOnPhoto,
} from "@/api/hooks";
import { useToast } from "@/features/toast";
import { useTryOnPhotosStore } from "../store/try-on-photos-store";

/** Tasarımdaki "JPG, PNG · Max 10MB" sınırı */
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/jpeg", "image/png"];
/** API henüz 5 dönse bile arayüz 4 slot gösterir. */
export const TRY_ON_PHOTO_LIMIT = 4;

/**
 * Backend 4xx'i api-client resolve ettiği için hata hem Axios
 * şeklinden hem de `{ success: false, message }` zarfından gelebilir.
 */
const extractApiMessage = (apiError: unknown): string | null => {
  if (!apiError || typeof apiError !== "object") return null;
  const err = apiError as {
    message?: string;
    response?: { data?: { message?: string } };
  };
  const fromResponse = err.response?.data?.message;
  if (typeof fromResponse === "string" && fromResponse.length > 0) {
    return fromResponse;
  }
  if (typeof err.message === "string" && err.message.length > 0) {
    return err.message;
  }
  return null;
};

/**
 * Try-On Photos Feature Hook
 * API + store entegrasyonu — component'lar bu hook'u doğrudan kullanır
 */
export const useTryOnPhotos = () => {
  const { t } = useTranslation();
  const { success, error } = useToast();

  const photosQuery = useTryOnPhotosQuery();
  const addMutation = useAddTryOnPhoto();
  const updateMutation = useUpdateTryOnPhoto();
  const deleteMutation = useDeleteTryOnPhoto();

  const { deletingPhoto, openDeleteDialog, closeDeleteDialog } =
    useTryOnPhotosStore();

  const photos = photosQuery.data?.data.photos ?? [];
  const limit = TRY_ON_PHOTO_LIMIT;
  const canAddMore = photos.length < limit;

  const handleAddPhoto = useCallback(
    async (file: File) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        error(t("settings.profile.tryOn.invalidFileType"));
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        error(t("settings.profile.tryOn.fileTooLarge"));
        return;
      }

      try {
        await addMutation.mutateAsync({ file });
        success(t("settings.profile.tryOn.uploadSuccess"));
      } catch (uploadError) {
        console.error("Try-on photo upload error:", uploadError);
        error(
          extractApiMessage(uploadError) ||
            t("settings.profile.tryOn.uploadError")
        );
      }
    },
    [addMutation, success, error, t]
  );

  const handleMarkAsSelf = useCallback(
    async (photoId: string) => {
      try {
        await updateMutation.mutateAsync({
          photoId,
          updates: { isSelf: true },
        });
        success(t("settings.profile.tryOn.mainPhotoSuccess"));
      } catch (updateError) {
        console.error("Try-on photo self update error:", updateError);
        error(
          extractApiMessage(updateError) ||
            t("settings.profile.tryOn.updateError")
        );
      }
    },
    [updateMutation, success, error, t]
  );

  const handleRenamePhoto = useCallback(
    async (photoId: string, nextName: string) => {
      const name = nextName.trim();
      if (!name) return;

      try {
        await updateMutation.mutateAsync({
          photoId,
          updates: { name },
        });
      } catch (renameError) {
        console.error("Try-on photo rename error:", renameError);
        error(
          extractApiMessage(renameError) ||
            t("settings.profile.tryOn.updateError")
        );
      }
    },
    [updateMutation, error, t]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingPhoto) return;

    try {
      await deleteMutation.mutateAsync(deletingPhoto.id);
      success(t("settings.profile.tryOn.deleteSuccess"));
      closeDeleteDialog();
    } catch (deleteError) {
      console.error("Try-on photo delete error:", deleteError);
      error(
        extractApiMessage(deleteError) || t("settings.profile.tryOn.deleteError")
      );
    }
  }, [deletingPhoto, deleteMutation, success, error, closeDeleteDialog, t]);

  return {
    photos,
    limit,
    canAddMore,

    isLoading: photosQuery.isLoading,
    isAdding: addMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    deletingPhoto,

    addPhoto: handleAddPhoto,
    markAsSelf: handleMarkAsSelf,
    renamePhoto: handleRenamePhoto,
    confirmDelete: handleDeleteConfirm,

    openDeleteDialog,
    closeDeleteDialog,
  };
};
